import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Restore (and keep saving) the scroll position of a scrollable element across
 * mounts, keyed in `sessionStorage`. Used by list pages so returning from a
 * detail view lands at the same position within the tab session.
 *
 * `selector` resolves the scroll container (the admin layout's scroll area is
 * stable across route changes). `ready` should turn true once the list content
 * has rendered, so the saved offset is applied against the final layout height.
 */
export function useScrollRestoration(selector, storageKey, ready) {
  const restored = useRef(false);

  // Restore once, after the content that gives the page its height is present.
  useLayoutEffect(() => {
    if (restored.current || !ready) return;
    const el = document.querySelector(selector);
    if (!el) return;
    let saved = null;
    try { saved = sessionStorage.getItem(storageKey); } catch { /* ignore */ }
    if (saved != null) el.scrollTop = Number(saved) || 0;
    restored.current = true;
  }, [selector, storageKey, ready]);

  // Track the live scroll position and persist it on scroll and on unmount, so
  // the latest offset is stored even when leaving the page.
  useEffect(() => {
    const el = document.querySelector(selector);
    if (!el) return undefined;
    const save = () => {
      try { sessionStorage.setItem(storageKey, String(el.scrollTop)); } catch { /* ignore */ }
    };
    el.addEventListener('scroll', save, { passive: true });
    return () => {
      save();
      el.removeEventListener('scroll', save);
    };
  }, [selector, storageKey]);
}
